import { CheckCircle2, CircleAlert, FilePlus2, Heart, LayoutList, LoaderCircle, Settings, Sparkles, UserRound } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { CreateJobForm } from "../features/svg-jobs/CreateJobForm";
import { JobList } from "../features/svg-jobs/JobList";
import { JobProgress } from "../features/svg-jobs/JobProgress";
import { JobStatusBadge } from "../features/svg-jobs/JobStatusBadge";
import { LoginPage } from "../routes/LoginPage";
import { SettingsPage } from "../routes/SettingsPage";
import type { SvgJob } from "../lib/types";

const baseTime = "2026-07-15T08:00:00.000Z";

function mockJob(overrides: Partial<SvgJob>): SvgJob {
  return {
    id: "38dc9e8f-cd67-42bd-a21d-a269597ecea1",
    user_id: "00000000-0000-4000-8000-000000000001",
    status: "completed",
    progress: "completed",
    progress_stage: "completed",
    progress_percent: 100,
    engine: "tikz",
    source_kind: "code",
    source_filename: "vector-plane.tex",
    source_path: "jobs/mock/input/vector-plane.tex",
    source_size_bytes: 1864,
    optimize: true,
    output_count: 1,
    output_path: "jobs/mock/output/output.svg",
    archive_path: "jobs/mock/output/result.zip",
    manifest_path: "jobs/mock/output/manifest.json",
    archive_size_bytes: 9840,
    error_message: null,
    github_run_id: 29349999999,
    github_run_attempt: 1,
    github_sha: "33f73d6a0e8215f715a5a56cb349abc564046f86",
    attempt_count: 1,
    is_favorite: false,
    source_job_id: null,
    created_at: baseTime,
    updated_at: "2026-07-15T08:01:28.000Z",
    uploaded_at: "2026-07-15T08:00:08.000Z",
    queued_at: "2026-07-15T08:00:11.000Z",
    started_at: "2026-07-15T08:00:24.000Z",
    rendering_at: "2026-07-15T08:00:42.000Z",
    uploading_at: "2026-07-15T08:01:11.000Z",
    completed_at: "2026-07-15T08:01:28.000Z",
    expires_at: "2026-08-14T08:00:00.000Z",
    last_transition_at: "2026-07-15T08:01:28.000Z",
    ...overrides,
  };
}

const jobs: SvgJob[] = [
  mockJob({ id: "11111111-1111-4111-8111-111111111111", source_filename: "vector-plane.tex", engine: "tikz", is_favorite: true }),
  mockJob({
    id: "22222222-2222-4222-8222-222222222222",
    source_filename: "sorting-flow.dot",
    engine: "graphviz",
    status: "building",
    progress: "rendering",
    progress_stage: "rendering",
    progress_percent: 65,
    output_count: 0,
    output_path: null,
    archive_path: null,
    manifest_path: null,
    archive_size_bytes: null,
    completed_at: null,
    updated_at: "2026-07-15T08:04:00.000Z",
    created_at: "2026-07-15T08:03:00.000Z",
    uploaded_at: "2026-07-15T08:03:07.000Z",
    queued_at: "2026-07-15T08:03:09.000Z",
    started_at: "2026-07-15T08:03:20.000Z",
    rendering_at: "2026-07-15T08:03:42.000Z",
    uploading_at: null,
  }),
  mockJob({ id: "33333333-3333-4333-8333-333333333333", source_filename: "service-architecture.d2", engine: "d2", source_kind: "file", created_at: "2026-07-14T09:20:00.000Z", completed_at: "2026-07-14T09:20:34.000Z", updated_at: "2026-07-14T09:20:34.000Z", output_count: 1 }),
  mockJob({
    id: "44444444-4444-4444-8444-444444444444",
    source_filename: "surface-plot.tex",
    engine: "pgfplots",
    status: "failed",
    progress: "failed",
    progress_stage: "failed",
    progress_percent: 65,
    output_count: 0,
    output_path: null,
    archive_path: null,
    manifest_path: null,
    archive_size_bytes: null,
    error_message: "LaTeX compilation failed near line 18: missing axis environment terminator.",
    created_at: "2026-07-13T14:00:00.000Z",
    completed_at: "2026-07-13T14:00:43.000Z",
    updated_at: "2026-07-13T14:00:43.000Z",
    uploading_at: null,
  }),
  mockJob({ id: "55555555-5555-4555-8555-555555555555", source_filename: "algorithm-pack.zip", engine: "auto", source_kind: "zip", output_count: 8, output_path: null, created_at: "2026-07-10T11:00:00.000Z", completed_at: "2026-07-10T11:02:12.000Z", updated_at: "2026-07-10T11:02:12.000Z" }),
];

const nav = [
  ["创建任务", "workspace", FilePlus2],
  ["全部任务", "jobs", LayoutList],
  ["进行中", "jobs", LoaderCircle],
  ["已完成", "jobs", CheckCircle2],
  ["异常任务", "jobs", CircleAlert],
  ["收藏", "jobs", Heart],
  ["设置", "settings", Settings],
] as const;

function VisualShell({ screen, children }: { screen: string; children: React.ReactNode }) {
  return (
    <div className="app-frame visual-shell">
      <aside className="sidebar">
        <div className="sidebar-brand"><span className="brand-icon"><Sparkles size={18} /></span><div><strong>SVG Render</strong><small>多引擎绘图工作台</small></div></div>
        <div className="sidebar-section-label">工作区</div>
        <nav className="sidebar-nav">
          {nav.map(([label, target, Icon], index) => <Link key={`${label}-${index}`} className={`sidebar-link ${screen === target && (index < 2 || label === "设置") ? "active" : ""}`} to={`/?screen=${target}`}><Icon size={17} /><span>{label}</span>{index > 0 && index < 6 && <b>{[0, 5, 1, 1, 1][index - 1]}</b>}</Link>)}
        </nav>
        <div className="sidebar-footer"><div className="realtime-state state-connected"><span />实时同步</div><div className="account-card"><span><UserRound size={16} /></span><div><small>当前账号</small><strong>demo@svg-render.dev</strong></div></div></div>
      </aside>
      <div className="app-content"><main id="main-content">{children}</main></div>
    </div>
  );
}

function VisualWorkspace() {
  return (
    <VisualShell screen="workspace">
      <div className="page-container" data-ui-capture="authenticated-workspace">
        <header className="page-header workspace-header"><div><span className="kicker">DIAGRAM WORKSPACE</span><h1>创建 SVG 绘图任务</h1><p>手动提交源码，实时查看 GitHub Actions 渲染进度。</p></div><span className="secondary-button">查看全部任务</span></header>
        <section className="stats-grid"><article><span className="stat-icon"><LayoutList size={18} /></span><div><small>全部任务</small><strong>5</strong></div></article><article><span className="stat-icon active"><LoaderCircle size={18} /></span><div><small>进行中</small><strong>1</strong></div></article><article><span className="stat-icon success"><CheckCircle2 size={18} /></span><div><small>已完成</small><strong>3</strong></div></article><article><span className="stat-icon danger"><CircleAlert size={18} /></span><div><small>异常</small><strong>1</strong></div></article></section>
        <CreateJobForm visualOnly />
        <section className="recent-section"><div className="section-heading"><div><span className="kicker">RECENT</span><h2>最近任务</h2><p>展示最近 5 条任务和当前阶段。</p></div><span className="ghost-button">全部任务</span></div><JobList jobs={jobs} /></section>
      </div>
    </VisualShell>
  );
}

function VisualJobs() {
  return (
    <VisualShell screen="jobs">
      <div className="page-container" data-ui-capture="jobs-list">
        <header className="page-header"><div><span className="kicker">TASK LIBRARY</span><h1>全部任务</h1><p>共 5 条任务，按创建时间分组。</p></div><span className="primary-button compact"><FilePlus2 size={16} />创建任务</span></header>
        <section className="filter-bar"><div className="search-box"><LayoutList size={17} /><input readOnly placeholder="搜索源文件、引擎或阶段" /></div><select defaultValue="all"><option value="all">全部任务</option></select></section>
        <JobList jobs={jobs} grouped />
      </div>
    </VisualShell>
  );
}

function VisualDetail() {
  const job = jobs[1]!;
  return (
    <VisualShell screen="detail">
      <div className="page-container" data-ui-capture="job-detail">
        <div className="detail-toolbar"><span className="ghost-button">← 任务列表</span><span className="secondary-button">刷新</span></div>
        <header className="detail-header"><div><div className="detail-badges"><JobStatusBadge job={job} /><span>第 1 次构建</span></div><h1>{job.source_filename}</h1><p>Graphviz DOT · 单文件</p></div><div className="detail-main-actions"><span className="secondary-button"><Heart size={16} />收藏</span></div></header>
        <section className="panel detail-progress-card"><div className="section-heading border-bottom"><div><span className="kicker">PIPELINE</span><h2>构建进度</h2><p>从私有上传、队列、绘制到结果保存的完整状态。</p></div><span className="run-attempt">Run 29349999999</span></div><div className="detail-card-body"><JobProgress job={job} /></div><div className="detail-actions-bar"><span className="secondary-button">源文件</span><span className="secondary-button">Action 日志</span></div></section>
        <div className="detail-grid"><section className="panel"><div className="section-heading border-bottom"><div><h2>输出预览</h2><p>完成后在此直接预览单 SVG。</p></div></div><div className="visual-svg-preview"><svg viewBox="0 0 560 250" role="img" aria-label="Graphviz 示例预览"><defs><marker id="arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><path d="M0,0 L10,3.5 L0,7 Z" /></marker></defs><g fill="none" stroke="currentColor" strokeWidth="2" markerEnd="url(#arrow)"><path d="M280 48 L155 112" /><path d="M280 48 L405 112" /><path d="M155 140 L92 202" /><path d="M155 140 L218 202" /><path d="M405 140 L342 202" /><path d="M405 140 L468 202" /></g><g><circle cx="280" cy="34" r="25" /><circle cx="155" cy="126" r="25" /><circle cx="405" cy="126" r="25" /><circle cx="92" cy="216" r="22" /><circle cx="218" cy="216" r="22" /><circle cx="342" cy="216" r="22" /><circle cx="468" cy="216" r="22" /></g></svg></div></section><section className="panel"><div className="section-heading border-bottom"><div><h2>任务信息</h2><p>生命周期和技术标识。</p></div></div><dl className="detail-list"><div className="detail-item"><dt>创建时间</dt><dd>2026/07/15 16:03:00</dd></div><div className="detail-item"><dt>当前阶段</dt><dd>正在调用绘图引擎</dd></div><div className="detail-item"><dt>文件保留至</dt><dd>2026/08/14 16:03:00</dd></div></dl></section></div>
      </div>
    </VisualShell>
  );
}

export function VisualTestApp() {
  const location = useLocation();
  const screen = new URLSearchParams(location.search).get("screen") || "workspace";
  if (screen === "login") return <LoginPage visualOnly />;
  if (screen === "jobs") return <VisualJobs />;
  if (screen === "detail") return <VisualDetail />;
  if (screen === "settings") return <VisualShell screen="settings"><SettingsPage /></VisualShell>;
  return <VisualWorkspace />;
}
